import { intlLocale } from '@lms/charts';
import { Card } from '@lms/ui/card';
import { cn } from '@lms/ui/utils';
import { BugIcon, LoaderCircle, TelescopeIcon } from 'lucide-react';
import type React from 'react';
import { useLocale, useTranslations } from 'use-intl';

/** Filter row above the dashboard grid. */
export function StatsToolbar({ children }: React.PropsWithChildren) {
  return (
    <div className="mx-auto mb-4 flex w-full max-w-screen-2xl flex-col gap-4 rounded-md border p-2 lg:flex-row lg:items-center lg:[&>*:last-child:not(:first-child)]:ml-auto">
      {children}
    </div>
  );
}

/** Three-column dashboard grid; `WIDE` cards span the full row. */
export function StatsGrid({ children }: React.PropsWithChildren) {
  return (
    <div className="mx-auto grid max-w-screen-2xl grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      {children}
    </div>
  );
}

export const WIDE = 'md:col-span-2 lg:col-span-3';

/** Full-width loading / error / empty state below the toolbar. */
export function StatsPlaceholder({
  state,
  error,
}: {
  state: 'loading' | 'error' | 'empty';
  error?: unknown;
}) {
  const t = useTranslations('stats');
  return (
    <div className="mx-auto flex max-w-screen-2xl items-center justify-center py-16">
      <Card
        className={cn(
          'flex flex-col items-center justify-center gap-4 p-8',
          state === 'error' ? 'text-destructive' : 'text-primary',
        )}
      >
        {state === 'loading' && (
          <LoaderCircle className="size-16 animate-spin" />
        )}
        {state === 'error' && (
          <>
            <BugIcon className="size-16" />
            {`Error: ${error}`}
          </>
        )}
        {state === 'empty' && (
          <>
            <TelescopeIcon className="size-16" />
            {t('no_data')}
          </>
        )}
      </Card>
    </div>
  );
}

/** Human label for the selected period ("2025", "March 2025", "Since start"). */
export function usePeriodLabel(period: string, fromDate?: Date): string {
  const t = useTranslations('stats');
  const locale = intlLocale(useLocale());
  if (period === 'since') return t('since_start');
  return (
    fromDate?.toLocaleDateString(
      locale,
      period === 'month'
        ? { year: 'numeric', month: 'long' }
        : { year: 'numeric' },
    ) ?? ''
  );
}
